/**
 * MatchPercent — how well a candidate fits a job, as "72% match".
 *
 * One definition of the score bands for every surface that shows a match: the applicants list,
 * the application workspace and the AI candidates table. They had grown one inline copy each,
 * and a band that moves in one place and not the others reads as two different scores.
 *
 * Null means "not scored", never 0%: a seeker with no profile vector has no score at all, and
 * printing 0% for them claimed a terrible match that nobody had measured.
 *
 * Why it is null comes from the backend's relevancyStatus. Without it a CV that had been read and
 * gave no text (a scan, a near-empty PDF) read "Scoring…" forever, and staff waited on a number
 * that was never coming.
 */
import { Chip, Tooltip, Typography } from '@mui/material';
import React from 'react';

export type MatchBand = 'high' | 'mid' | 'low';

export const matchBand = (score: number): MatchBand =>
  score >= 75 ? 'high' : score >= 50 ? 'mid' : 'low';

/** Foreground for a score rendered as plain text, where a pill would be too heavy. */
export const matchColor = (score: number): string => {
  const band = matchBand(score);
  return band === 'high'
    ? 'var(--color-success)'
    : band === 'mid'
      ? 'var(--color-warning)'
      : 'var(--color-text-muted)';
};

const PILL: Record<MatchBand, { backgroundColor: string; color: string }> = {
  high: { backgroundColor: 'var(--green-tint)', color: 'var(--green)' },
  mid: { backgroundColor: 'var(--amber-tint)', color: 'var(--amber)' },
  low: { backgroundColor: 'var(--grey-tint)', color: 'var(--grey)' },
};

/** The backend's relevancyStatus. */
export type MatchStatus = 'SCORED' | 'PENDING' | 'CV_UNREADABLE' | 'NO_RESUME';

export interface UnscoredReason {
  label: string;
  hint: string;
}

/**
 * What to say when there is no score. Falls back to "is there a CV" for a record that predates
 * relevancyStatus, which was the whole rule before.
 */
export const unscoredReason = (status: string | null | undefined, hasResume: boolean): UnscoredReason => {
  if (status === 'CV_UNREADABLE') {
    return {
      label: 'CV unreadable',
      hint: 'No readable text in this CV, often a scanned image. Ask the candidate for a text-based PDF or Word file.',
    };
  }
  if (status === 'NO_RESUME' || (!status && !hasResume)) {
    return { label: 'No resume', hint: 'No CV on file, so there is nothing to score.' };
  }
  return { label: 'Scoring\u2026', hint: 'The CV is queued to be read. The score usually appears within a few minutes.' };
};

interface MatchPercentProps {
  /** 0 to 100, or null when the pair has not been scored. */
  value: number | null | undefined;
  /** relevancyStatus: why there is no score. */
  status?: string | null;
  hasResume?: boolean;
}

const MatchPercent: React.FC<MatchPercentProps> = ({ value, status, hasResume = true }) => {
  if (value == null) {
    const reason = unscoredReason(status, hasResume);
    // Only an unreadable CV is spelled out in a list: it is the one a recruiter has to act on.
    return (
      <Tooltip title={reason.hint}>
        <Typography component="span" variant="body2" color="textSecondary" aria-label={reason.hint}>
          {status === 'CV_UNREADABLE' ? reason.label : '-'}
        </Typography>
      </Tooltip>
    );
  }
  const pct = Math.round(value);
  return (
    <Chip
      size="small"
      label={`${pct}% match`}
      data-band={matchBand(pct)}
      sx={PILL[matchBand(pct)]}
    />
  );
};

export default MatchPercent;
